// PayPal Smart Buttons (with Google Pay) — falls back to PayPal.Me when not configured
import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api from "../lib/api";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

export default function PayPalCheckout({ purpose, amountUSD, refId, meta = {}, label = "Pay with PayPal", onSuccess }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/paypal/config").then(({ data }) => setCfg(data)); }, []);

  if (!cfg) return <div className="text-[10px] text-white/40 tracking-wider">Loading PayPal…</div>;

  // Fallback: PayPal.Me redirect
  if (!cfg.configured) {
    const handlePay = async () => {
      try {
        const { data } = await api.post("/paypal/create_order", { purpose, amount_usd: amountUSD, ref_id: refId, meta });
        window.open(data.redirect, "_blank", "noopener");
        toast.info("PayPal.Me opened in new tab. After paying, click 'I paid' below.");
        // Save id for confirmation step
        const evt = new CustomEvent("paypal-me-order", { detail: { id: data.id, purpose, refId, amountUSD } });
        window.dispatchEvent(evt);
        if (window.confirm(`After paying ${amountUSD.toFixed(2)} AUD via PayPal.Me, click OK to provisionally credit your account. (Owner reconciles within 24h.)`)) {
          await api.post(`/paypal/me_confirm/${data.id}`);
          toast.success("Provisionally credited — enjoy!");
          onSuccess?.(data.id);
        }
      } catch (e) { toast.error(e?.response?.data?.detail || "PayPal redirect failed"); }
    };
    return (
      <div className="space-y-2" data-testid="paypal-fallback">
        <button onClick={handlePay} className="btn-nexus w-full justify-center" data-testid="paypal-me-btn">
          <ExternalLink className="w-3.5 h-3.5" /> {label} · ${amountUSD.toFixed(2)} via PayPal.Me
        </button>
        <div className="text-[10px] text-white/40 leading-relaxed">
          Owner: configure Smart Buttons + Google Pay by adding <code className="text-nexus-cyan">PAYPAL_CLIENT_ID</code> & <code className="text-nexus-cyan">PAYPAL_SECRET</code> in Owner HQ → Integrations.
        </div>
      </div>
    );
  }

  // Real Smart Buttons + Google Pay (Advanced Checkout).
  // Google Pay isn't wrapped by @paypal/react-paypal-js — we render its button in
  // a plain <div> once the SDK loads. It uses the SAME /paypal/create_order and
  // /paypal/capture_order endpoints as PayPal Buttons.
  return (
    <div data-testid="paypal-smart-buttons">
      <PayPalScriptProvider options={{
        "client-id": cfg.client_id,
        currency: "AUD",
        intent: "capture",
        components: "buttons,funding-eligibility,googlepay",
        "enable-funding": "venmo,paylater,card",
      }}>
        <PayPalButtons
          style={{ layout: "vertical", shape: "rect", color: "gold", label: "pay" }}
          createOrder={async () => {
            const { data } = await api.post("/paypal/create_order", { purpose, amount_usd: amountUSD, ref_id: refId, meta });
            return data.id;
          }}
          onApprove={async (data) => {
            await api.post(`/paypal/capture_order/${data.orderID}`);
            toast.success("Payment captured · fulfilled");
            onSuccess?.(data.orderID);
          }}
          onError={(err) => { console.error(err); toast.error("PayPal error"); }}
        />
        <GooglePayButton
          amountUSD={amountUSD}
          purpose={purpose}
          refId={refId}
          meta={meta}
          onSuccess={onSuccess}
        />
      </PayPalScriptProvider>
      <div className="text-[10px] text-white/40 mt-2">PayPal · Google Pay · Cards · {cfg.mode}</div>
    </div>
  );
}

/** Google Pay via PayPal Advanced Checkout — waits for window.paypal.Googlepay to appear,
 *  fetches its config, mounts the button in a scoped container, and routes the payment
 *  through the same /paypal/create_order + /paypal/capture_order flow. */
function GooglePayButton({ amountUSD, purpose, refId, meta, onSuccess }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      if (!mounted) return;
      const paypal = window.paypal;
      const googleReady = window.google?.payments?.api?.PaymentsClient;
      if (!paypal?.Googlepay || !googleReady) {
        if (tries > 25) { clearInterval(iv); }
        return;
      }
      clearInterval(iv);
      try {
        const gpay = paypal.Googlepay();
        const cfg = await gpay.config();
        const paymentsClient = new window.google.payments.api.PaymentsClient({ environment: cfg.environment || "PRODUCTION" });
        const isReady = await paymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: cfg.allowedPaymentMethods,
        });
        if (!isReady?.result) return; // device / browser can't do Google Pay
        setReady(true);
        const button = paymentsClient.createButton({
          buttonColor: "black",
          buttonType: "pay",
          buttonSizeMode: "fill",
          onClick: async () => {
            try {
              const { data } = await api.post("/paypal/create_order", { purpose, amount_usd: amountUSD, ref_id: refId, meta });
              const paymentData = await paymentsClient.loadPaymentData({
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: cfg.allowedPaymentMethods,
                merchantInfo: cfg.merchantInfo,
                transactionInfo: {
                  countryCode: cfg.countryCode || "AU",
                  currencyCode: "AUD",
                  totalPriceStatus: "FINAL",
                  totalPrice: amountUSD.toFixed(2),
                },
                callbackIntents: [],
              });
              await gpay.confirmOrder({ orderId: data.id, paymentMethodData: paymentData.paymentMethodData });
              await api.post(`/paypal/capture_order/${data.id}`);
              toast.success("Google Pay · captured");
              onSuccess?.(data.id);
            } catch (err) {
              if (err?.statusCode !== "CANCELED") toast.error("Google Pay failed");
              console.warn("[gpay]", err);
            }
          },
        });
        const holder = document.getElementById("nexus-gpay-btn");
        if (holder && !holder.hasChildNodes()) holder.appendChild(button);
      } catch (e) { console.warn("[gpay init]", e); }
    }, 400);
    return () => { mounted = false; clearInterval(iv); };
  }, [amountUSD, purpose, refId, meta, onSuccess]);
  return (
    <div className={`mt-2 ${ready ? "" : "hidden"}`} data-testid="google-pay-container">
      <div id="nexus-gpay-btn" />
    </div>
  );
}
